import React, { useMemo, useState } from "react";
import { Badge } from "@shared/ui/badge";
import { EyeIcon, Redo } from "lucide-react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn, TableAction } from "@shared/types/table";
import { useLocation, useNavigate } from "react-router-dom";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { useBillCorrectionList } from "../hook";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { PageHeader } from "@shared/components/PageHeader";

interface MeterRun {
  id: number;
  meterSchedule: {
    id: number;
    meterCycle: {
      id: number;
      meterCycleName: string;
      meterType: string;
      productCode: string[];
    };
  };
  summary: {
    meterCount: number;
    consumerCount: number;
    readingCount: number;
    rcntCount: number;
    faultyCount: number;
    normalCount: number;
  };
  status: string;
  month: string;
  lastRunDate: string;
  nextRunDate: string;
  completionPercentage: number;
  dispatchStatus: string;
}

const formatMonthToYearMonth = (month: string): string => {
  if (!month) return "";
  const currentYear = new Date().getFullYear();
  const map: Record<string, string> = {
    january: "01",
    february: "02",
    march: "03",
    april: "04",
    may: "05",
    june: "06",
    july: "07",
    august: "08",
    september: "09",
    october: "10",
    november: "11",
    december: "12",
  };
  return map[month.toLowerCase()]
    ? `${currentYear}-${map[month.toLowerCase()]}`
    : "";
};

const CorrectionList: React.FC = () => {
  localStorage.removeItem("bill-correction");
  const currency = useCurrency();
  const navigate = useNavigate();
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = PAGE_SIZE;
  const remoteUtilityId = getRemoteUtilityId();
  const { data: correctionList } = useBillCorrectionList(
    remoteUtilityId,
    searchQuery,
    currentPage,
  );

  // --- handlers ---
  const handleFilterSubmit = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSearch = (term: string) => setSearchQuery(term);

  const handlePageChange = (p: number) => setCurrentPage(p);

  const location = useLocation();

  const handleViewBill = (row) => {
    const params = new URLSearchParams(location.search);
    params.set("correction_id", row?.correctionId);

    navigate(`/bx/bill-detail/${row.correctedBillId}/?${params.toString()}`);
  };
  const handleContinueDraft = (row: any) => {
    navigate(
      `/bx/bill-generation/bill-correction/correct-bill?action=draft&correction_id=${row.correctionId}`,
    );
  };
  // --- columns ---
  const columns: TableColumn<MeterRun>[] = [
    {
      key: "originalInvoice",
      header: "Original Invoice",
    },
    { key: "originalBillDate", header: "Original Date", type: "text" },
    {
      key: "correctedInvoice",
      header: "Corrected Invoice",
    },
    {
      key: "correctedInvoiceDate",
      header: "Corrected Date",
    },
    {
      key: "consumerNo",
      header: "Account No",
    },
    {
      key: "consumerName",
      header: "Consumer Name",
      render: (value, row: any) => {
        return (
          <span className="text-sm text-grey-700">
            {row.firstName} {row.lastName}
          </span>
        );
      },
    },
    {
      key: "originalAmount",
      header: "Original Amount",
      type: "text",
      render: (value) => (
        <span>
          {currency} {value}
        </span>
      ),
    },
    {
      key: "correctedAmount",
      header: "Corrected Amount",
      type: "text",
      render: (value) => (
        <span>
          {currency} {value}
        </span>
      ),
    },
  ];

  const actions: TableAction[] = [
    {
      label: "View Bill",
      icon: <EyeIcon size={20} className="text-gray-500" />,
      onClick: (row) => handleViewBill(row),
      hidden: (row) => row.status === "Draft",
    },
    {
      label: "Continue",
      onClick: (row) => handleContinueDraft(row),
      icon: <Redo size={20} className="text-gray-500" />,
      hidden: (row) => row.status !== "Draft",
    },
  ];

  const totalItems = 10;
  const totalPages = Math.ceil(totalItems / pageSize);
  const Filters = () => {
    return <div></div>;
  };
  return (
    <div className="space-y-4">
      <PageHeader
        title="Billing Correction"
        subtitle="View history of corrected bills and process new corrections."
        buttonName="Correct Bill"
        handleSubmit={() =>
          navigate(`/bx/bill-generation/bill-correction/correct-bill`)
        }
      />
      <GenericTable
        data={correctionList?.result}
        columns={columns}
        rowKey="id"
        pagination={{
          currentPage,
          totalPages: Math.ceil(correctionList?.count / PAGE_SIZE),
          totalItems: correctionList?.count,
          itemsPerPage: PAGE_SIZE,
          startItem: (currentPage - 1) * PAGE_SIZE + 1,
          endItem: Math.min(currentPage * PAGE_SIZE, correctionList?.count),
        }}
        onPageChange={handlePageChange}
        onSearch={handleSearch}
        searchPlaceholder="Search by Invoice number..."
        actions={actions}
        actionsType="icons"
        filterPanel={<Filters />}
        showFilters={true}
        emptyMessage="No bill corrected yet."
        loading={false}
      />
    </div>
  );
};

export default CorrectionList;
